import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import NumberInput from "@/components/atoms/NumberInput";
import SegmentedControls from "@/components/atoms/SegmentedControls";
import Text from "@/components/atoms/Text";
import { formatNumber, getNumberSeparators } from "@/components/organisms/InspectionModal/utils";
import { WeightUnit } from "@/types";
import { toKg, WEIGHT_UNIT_LABELS, WeightInputValue } from "@/utils/weight";
import styles from "./styles.module.css";

export interface WeightInputProps {
  value: WeightInputValue;
  onChange: (value: WeightInputValue) => void;
  label?: ReactNode;
  "aria-label"?: string;
  withAsterisk?: boolean;
  error?: ReactNode;
  disabled?: boolean;
  /** Puts `data-autofocus` on the number field, so a Mantine Modal focuses it when it opens. */
  autoFocusInModal?: boolean;
  /** Number field: `${testId}`. Toggle: `${testId}-unit`. Conversion line: `${testId}-conversion`. */
  "data-testid"?: string;
}

const UNIT_OPTIONS = [
  { value: "KG", label: WEIGHT_UNIT_LABELS.KG },
  { value: "TONNES", label: WEIGHT_UNIT_LABELS.TONNES },
];

/**
 * A weight typed in kg or tonnes: the number field, the unit toggle to its right and, under both, the same weight in
 * the other unit. Switching the unit keeps the typed number and changes only what it means.
 */
export default function WeightInput({
  value,
  onChange,
  label,
  "aria-label": ariaLabel,
  withAsterisk,
  error,
  disabled,
  autoFocusInModal,
  "data-testid": testId,
}: WeightInputProps) {
  const { t, i18n } = useTranslation();
  // The toggle is a radio group of its own, so it needs a name, and the field's name tells the groups in a list apart.
  const fieldName = ariaLabel ?? (typeof label === "string" ? label : undefined);
  const unitLabel = fieldName ? `${fieldName}, ${t("common:unit")}` : t("common:unit");

  return (
    <NumberInput
      {...getNumberSeparators(i18n.language)}
      // No decimalScale: extra decimals must reach validation, not be cut off while typing.
      allowNegative={false}
      value={value.amount}
      onChange={(amount) => onChange({ ...value, amount })}
      label={label}
      aria-label={ariaLabel}
      withAsterisk={withAsterisk}
      error={error}
      disabled={disabled}
      data-autofocus={autoFocusInModal ? true : undefined}
      data-testid={testId}
      classNames={{ wrapper: styles.field }}
      inputContainer={(input) => (
        <>
          <div className={styles.row}>
            {input}
            <SegmentedControls
              className={styles.unit}
              aria-label={unitLabel}
              data={UNIT_OPTIONS}
              value={value.unit}
              onChange={(unit) => onChange({ ...value, unit: unit as WeightUnit })}
              disabled={disabled}
              data-testid={testId && `${testId}-unit`}
            />
          </div>
          <Conversion value={value} language={i18n.language} data-testid={testId && `${testId}-conversion`} />
        </>
      )}
    />
  );
}

interface ConversionProps {
  value: WeightInputValue;
  language: string;
  "data-testid"?: string;
}

/** The typed weight in the other unit, e.g. "= 5 000 kg". Nothing while the field is empty. */
const Conversion = ({ value, language, "data-testid": testId }: ConversionProps) => {
  const kg = toKg(value);
  if (kg === null) return null;

  const other: WeightUnit = value.unit === "KG" ? "TONNES" : "KG";
  const amount = other === "TONNES" ? kg / 1000 : kg;
  return (
    <Text size="sm" color="secondary" className={styles.conversion} data-testid={testId}>
      = {formatNumber(amount, language, { decimals: 3 })} {WEIGHT_UNIT_LABELS[other]}
    </Text>
  );
};
